import React from 'react';
import './ButtonGroup.css';
import Tooltip from './Tooltip';

/**
 * Generic single-select button group.
 * items: [{ value: string, icon?: ReactNode, label?: string, badge?: number, tooltip?: string }]
 * At least one of icon/label should be provided for each item.
 * Optional badge: finite number shown in a circle to the right of the label (e.g. active criteria count).
 * Optional tooltip: non-empty string wraps the button in Tooltip.
 */
const ButtonGroup = ({
    items = [],
    value,
    onChange,
    ariaLabel,
    className = '',
}) => {
    if (!items || items.length === 0) return null;

    const handleClick = (itemValue) => {
        if (itemValue === value) return;
        if (onChange) onChange(itemValue);
    };

    return (
        <div
            className={`button-group ${className}`}
            role="radiogroup"
            aria-label={ariaLabel}
        >
            {items.map((item, idx) => {
                if (!item || (!item.icon && !item.label)) return null;
                const key = item.value ?? idx;
                const isActive = item.value === value;
                const button = (
                    <button
                        type="button"
                        className={`button-group-item${isActive ? ' button-group-item--active' : ''}`}
                        onClick={() => handleClick(item.value)}
                        aria-pressed={isActive}
                        aria-label={item.ariaLabel}
                    >
                        {item.icon && (
                            <span className="button-group-item-icon">
                                {item.icon}
                            </span>
                        )}
                        {item.label && (
                            <span className="button-group-item-label">
                                {item.label}
                            </span>
                        )}
                        {Number.isFinite(item.badge) && (
                            <span className="button-group-item-badge" aria-hidden="true">
                                {item.badge}
                            </span>
                        )}
                    </button>
                );
                return (
                    <React.Fragment key={key}>
                        {item.tooltip ? (
                            <Tooltip content={item.tooltip}>{button}</Tooltip>
                        ) : (
                            button
                        )}
                    </React.Fragment>
                );
            })}
        </div>
    );
};

export default ButtonGroup;

